import * as React from 'react';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {OptionProps} from 'react-select';

import moment from 'moment';
import _ from 'underscore';

import {colors} from '../colors/colors';
import {getColorNameFromTagColorDark} from '../colors/helpers';
import {SelectInlineCreatable} from '../select-inline/select-inline-creatable';

import {getTooltipText} from './helpers';

import styles from './tags-editor.module.css';

export interface User {
    id: string;
    name: string;
}

export type Tag = {
    id: string;
    name: string;
    count?: number;
    color: string;
    creator:
        | {
              id: string;
              __typename: 'User' | 'Origin';
              name: string;
          }
        | null
        | undefined;
    createdTime: number | null | undefined;
};

type Props = {
    appliedTags: Tag[];
    // If this is passed, we'll populate the dropdown w/ those tags before the user types anything
    defaultOptions?: any[];
    fetchTags: (searchTerm: string, currentTags: Tag[]) => Promise<any>;
    maxItems?: number;
    onUpdateTags: (arg1: Tag[]) => void;
    user: User;
};

const NEW_TAG_ID = 'NEW_TAG_ID';
let newTagCount = 0;

export function TagsEditor(props: Props) {
    const [appliedTags, setAppliedTags] = React.useState<Tag[]>(props.appliedTags);

    const onUpdateTagsDebounced = _.debounce((tags: Tag[]) => {
        props.onUpdateTags(tags);
    }, 200);

    const handleUpdateTags = (tags: Tag[]) => {
        if (!tags.length) {
            setAppliedTags(tags);
        } else {
            const newestTag = tags[tags.length - 1];
            // @ts-expect-error
            newestTag.creator = {id: props.user.id, __typename: 'User', name: props.user.name};
            // @ts-expect-error
            newestTag.createdTime = moment().unix();
            // @ts-expect-error
            newestTag.count = 0;
            const tagsWithCreatorInfo = tags.map((tag, index) =>
                index === tags.length - 1 ? newestTag : tag
            );

            // @ts-expect-error
            setAppliedTags(tagsWithCreatorInfo);
        }

        onUpdateTagsDebounced(tags);
    };

    const handleAddNewTag = (newTag: string) => {
        const newTagList = appliedTags.concat([
            {
                name: newTag,
                count: 0,
                id: getNewTagId(),
                color: 'COLOR_DEFAULT',
                creator: {id: props.user.id, __typename: 'User', name: props.user.name},
                createdTime: moment().unix(),
            },
        ]);
        handleUpdateTags(newTagList);
    };

    React.useEffect(() => {
        setAppliedTags(props.appliedTags);
    }, [props.appliedTags]);

    return (
        <div className={styles.container}>
            <SelectInlineCreatable
                placeholder='Add tags…'
                value={appliedTags.map((tag) => ({
                    ...tag,
                    colorName: getColorNameFromTagColorDark(tag.color),
                    tooltipText: getTooltipText(tag),
                }))}
                multi={true}
                maxItems={props.maxItems}
                // If array of default options is passed we want to use it
                // otherwise let the user type to search
                // See: https://react-select.com/async#defaultoptions
                defaultOptions={props.defaultOptions || true}
                loadOptions={(searchTerm) => props.fetchTags(searchTerm, appliedTags)}
                onChange={handleUpdateTags}
                onBlur={() => {
                    props.onUpdateTags(appliedTags);
                }}
                tokenizedStyle='leftBorder'
                transparentBackground={true}
                closeMenuOnSelect={false}
                onCreate={handleAddNewTag}
                components={{
                    Option: OptionComponent,
                }}
                styles={{
                    // @ts-expect-error
                    valueContainer: (base) => ({...base, padding: '2px 0'}),
                    // @ts-expect-error
                    placeholder: (base) => ({...base, marginLeft: 0}),
                    // @ts-expect-error
                    input: (base) => ({...base, marginLeft: 0}),
                    // Carousel has a max z-index of 21
                    // @ts-expect-error
                    menu: (base) => ({...base, zIndex: 22}),
                }}
                createOptionPosition='last'
            />
        </div>
    );
}

const getNewTagId = (): string => {
    newTagCount += 1;

    return `${NEW_TAG_ID}-${newTagCount}`;
};

const OptionComponent = (props: OptionProps) => {
    // It seems that `labelKey` is not respected when creating new options,
    // so we need to look at `label` as well.
    const createNewText = props.data.label;

    return (
        <components.Option {...props}>
            <div className={styles['option-container']}>
                <div className={styles['option-title']}>
                    <div
                        style={{
                            backgroundColor: colors[getColorNameFromTagColorDark(props.data.color)],
                        }}
                        className={styles.dot}
                    ></div>
                    {props.data.name || createNewText}
                </div>
                <div className={styles['option-subtitle']}>{props.data.count}</div>
            </div>
        </components.Option>
    );
};
